import { useRefCallback } from "@enfusion-ui/hooks";
import React from "react";

import {
  ControllerBroadcastHandler,
  ModuleEntry,
  SocketData,
} from "../types/socket";
import { OMSModule } from "./oems/oems-socket-controller";
import { ReportModule } from "./reports/reports-socket-controller";

const modules = {
  REPORTS: {
    enabled: false,
    controller: ReportModule,
  } as ModuleEntry,
  OEMS: {
    enabled: false,
    controller: OMSModule,
  } as ModuleEntry,
} as const;

const useController = (moduleKey: keyof typeof modules) => {
  const [messages, setMessages] = React.useState<SocketData[]>([]);

  const callbackRef = React.useRef<ControllerBroadcastHandler[]>([]);
  const credentialRef = React.useRef<Record<string, string>>();
  const isSocketOpened = React.useRef<boolean>(false);
  const preOpenMessages = React.useRef<Array<Record<string, unknown>>>([]);

  const { enable, disable, onMessage } = React.useMemo(
    () => modules[moduleKey]?.controller(),
    [moduleKey]
  );

  const enableModule = useRefCallback(
    (userName: string, passCode?: string, url?: string) => {
      enable(
        broadcastMessage,
        (url ?? window.location.host)
          .replace("http://", "")
          .replace("https://", "")
      );
      credentialRef.current = {
        userName,
        passCode: passCode ?? "",
      };
    },
    [enable]
  );

  const disableModule = useRefCallback(() => {
    isSocketOpened.current = false;
    disable();
  }, [disable]);

  const broadcastMessage = useRefCallback(
    (message: Record<string, unknown>) => {
      const currentTime = new Date().toLocaleTimeString();
      const { command } = message;

      if (command === "log-message") {
        setMessages((prev) => [
          ...prev,
          {
            data: `${message.payload}`,
            length: `${message.payload}`.length,
            timestamp: currentTime,
            direction: "outgoing",
          },
        ]);
        return;
      }
      if (command === "save-oems-session-token") {
        const { applicationSessionToken } = message.payload as any;
        const applicationSessionStorageKey = [
          "applicationSessionToken",
          credentialRef.current?.userName,
        ]
          .filter(Boolean)
          .join("///");
        sessionStorage.setItem(
          applicationSessionStorageKey,
          applicationSessionToken
        );
        return;
      }

      if (command === "socket-open") {
        isSocketOpened.current = true;
        onMessage({ command, payload: credentialRef.current });

        setTimeout(() => {
          for (const preOpenMessage of preOpenMessages.current) {
            onMessage(preOpenMessage);
          }
          preOpenMessages.current = [];
        }, 500);
      } else {
        for (let callback of callbackRef.current) {
          try {
            callback(message);
          } catch (err) {
            console.error("Broadcast Subscription Error", err);
          }
        }
      }
      const socketMsg = JSON.stringify(message);

      setMessages((prev) => [
        ...prev,
        {
          data: socketMsg,
          length: socketMsg.length,
          timestamp: currentTime,
          direction: "incoming",
        },
      ]);
    },
    [onMessage, setMessages]
  );

  const postMessage = useRefCallback(
    (message: Record<string, any>) => {
      const currentTime = new Date().toLocaleTimeString();
      if (isSocketOpened.current) {
        onMessage(message);
        const socketMsg = JSON.stringify(message);
        setMessages((prev) => [
          ...prev,
          {
            data: socketMsg,
            length: socketMsg.length,
            timestamp: currentTime,
            direction: "outgoing",
          },
        ]);
      } else {
        preOpenMessages.current.push(message);
      }
    },
    [onMessage, setMessages]
  );

  const subscribeMessage = useRefCallback(
    (callback: ControllerBroadcastHandler) => {
      callbackRef.current = [
        ...callbackRef.current.filter((i) => i !== callback),
        callback,
      ];
      return () => {
        callbackRef.current = [
          ...callbackRef.current.filter((i) => i !== callback),
        ];
      };
    },
    [moduleKey]
  );

  return {
    enableModule,
    disableModule,
    subscribeMessage,
    postMessage,
    messages,
  };
};

export default useController;
